import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { validateAdminApiKey } from '../../services/adminAuthService';
import { AdminAuthCredentials } from '../../types/adminRoute';

interface AdminAuthState {
  isAdminAuthenticated: boolean;
  adminApiKey: string | null;
  loading: boolean;
  error: string | null;
}

// Load initial state from localStorage
const loadInitialState = (): AdminAuthState => {
  try {
    const savedAdminAuth = localStorage.getItem('beesort_admin_auth');
    if (savedAdminAuth) {
      const { adminApiKey } = JSON.parse(savedAdminAuth);
      if (adminApiKey) {
        return {
          isAdminAuthenticated: true,
          adminApiKey,
          loading: false,
          error: null,
        };
      }
    }
  } catch (error) {
    // If there's an error reading from localStorage, clear it
    localStorage.removeItem('beesort_admin_auth');
  }

  return {
    isAdminAuthenticated: false,
    adminApiKey: null,
    loading: false,
    error: null,
  };
};

const initialState: AdminAuthState = loadInitialState();

// Async thunk for admin authentication
export const authenticateAdmin = createAsyncThunk(
  'adminAuth/authenticate',
  async (credentials: AdminAuthCredentials) => {
    const response = await validateAdminApiKey(credentials);
    if (!response.success) {
      throw new Error(response.message || 'Admin authentication failed');
    }
    return { adminApiKey: credentials.adminApiKey };
  }
);

const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    adminLogout: (state) => {
      state.isAdminAuthenticated = false;
      state.adminApiKey = null;
      state.error = null;
      localStorage.removeItem('beesort_admin_auth');
    },
    clearAdminError: (state) => {
      state.error = null;
    },
    // For cases where we want to set admin key directly (e.g., from stored value)
    setAdminApiKey: (state, action) => {
      state.isAdminAuthenticated = true;
      state.adminApiKey = action.payload;
      state.error = null;
      localStorage.setItem('beesort_admin_auth', JSON.stringify({
        adminApiKey: action.payload
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authenticateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAdminAuthenticated = true;
        state.adminApiKey = action.payload.adminApiKey;
        state.error = null;
        // Save to localStorage
        localStorage.setItem('beesort_admin_auth', JSON.stringify({
          adminApiKey: action.payload.adminApiKey
        }));
      })
      .addCase(authenticateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.isAdminAuthenticated = false;
        state.adminApiKey = null;
        state.error = action.error.message || 'Admin authentication failed';
        localStorage.removeItem('beesort_admin_auth');
      });
  },
});

export const { adminLogout, clearAdminError, setAdminApiKey } = adminAuthSlice.actions;
export default adminAuthSlice.reducer; 